import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ChevronLeft, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import ProfielBewerkenSection from "./ProfielBewerkenSection";
import MijnAankopenSection from "./MijnAankopenSection";
import SlimAanbevelingCard from "./SlimAanbevelingCard";
import MijnFacturenSection from "../../components/MijnFacturenSection";
import BottomNav from "../../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

function ChangePasswordSection({ user }) {
  const { loginWithToken } = useAuth();
  const [newPass, setNewPass]       = useState("");
  const [confirm, setConfirm]       = useState("");
  const [showNew, setShowNew]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPass !== confirm) { setError("Wachtwoorden komen niet overeen"); return; }
    if (newPass.length < 8)  { setError("Wachtwoord moet minimaal 8 tekens bevatten"); return; }
    setSaving(true); setError(""); setSuccess(false);
    try {
      const r = await fetch(`${API}/api/user/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ new_password: newPass, confirm_password: confirm }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Er is iets misgegaan");
      // Vers token opslaan (oude sessies zijn ingetrokken) zodat je ingelogd blijft
      if (d.token) loginWithToken({ ...user, has_password: true }, d.token);
      setSuccess(true);
      setNewPass(""); setConfirm("");
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="account-card mt-6">
      <div className="flex items-center gap-2 mb-5">
        <Lock size={14} style={{ color: "#C9A84C" }} />
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">
          {user?.has_password ? "Wachtwoord wijzigen" : "Wachtwoord instellen"}
        </p>
      </div>
      <p className="text-zinc-500 text-sm mb-5">
        {user?.has_password
          ? "Stel een nieuw wachtwoord in voor je account."
          : "Je hebt nog geen wachtwoord ingesteld. Stel er nu een in om makkelijker in te loggen."}
      </p>
      <form onSubmit={handleSubmit} className="space-y-3 max-w-sm" data-testid="change-password-form">
        <div>
          <label className="course-label">Nieuw wachtwoord</label>
          <div className="relative">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input type={showNew ? "text" : "password"} value={newPass}
              onChange={e => { setNewPass(e.target.value); setError(""); }}
              placeholder="Minimaal 8 tekens"
              data-testid="new-password-input"
              className="course-input pl-9 pr-10" />
            <button type="button" onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              {showNew ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        </div>
        <div>
          <label className="course-label">Bevestig wachtwoord</label>
          <div className="relative">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input type={showConfirm ? "text" : "password"} value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(""); }}
              placeholder="Herhaal je wachtwoord"
              data-testid="confirm-password-input"
              className="course-input pl-9 pr-10" />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              {showConfirm ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        </div>
        {error && <p data-testid="change-password-error" className="text-red-400 text-sm">{error}</p>}
        {success && (
          <p data-testid="change-password-success" className="text-green-400 text-sm flex items-center gap-1.5">
            <CheckCircle size={14} /> Wachtwoord succesvol gewijzigd!
          </p>
        )}
        <button type="submit" disabled={saving || !newPass || !confirm}
          data-testid="change-password-submit"
          className="btn-gold-course px-6 py-2.5 text-sm font-bold disabled:opacity-40">
          {saving ? "Opslaan..." : user?.has_password ? "Wachtwoord wijzigen" : "Wachtwoord instellen"}
        </button>
      </form>
    </div>
  );
}

export default function ProfielPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isBedrijfsSporter = user?.role === "sporter";

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-2xl mx-auto px-4 pt-5 pb-28">
        <button onClick={() => navigate("/account")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-semibold mb-6 transition-colors">
          <ChevronLeft size={16} /> Profiel
        </button>

        <h1 className="text-white font-black text-2xl mb-6">Instellingen</h1>

        {/* Wachtwoord wijzigen */}
        <ChangePasswordSection user={user} />

        {/* Profiel bewerken — niet voor bedrijfssporters */}
        {!isBedrijfsSporter && (
          <div className="mt-4">
            <ProfielBewerkenSection user={user} />
          </div>
        )}

        {/* Mijn aankopen — alleen directe gebruikers */}
        {!isBedrijfsSporter && user?.role === "user" && (
          <div className="mt-4">
            <MijnAankopenSection user={user} />
          </div>
        )}

        {/* Slim aanbeveling */}
        {!isBedrijfsSporter && (
          <SlimAanbevelingCard user={user} />
        )}

        {/* Mijn facturen — niet voor bedrijfssporters */}
        {!isBedrijfsSporter && (
          <div className="account-card mt-4">
            <MijnFacturenSection token={user?.token} user={user} />
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
